import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wallet, CircleAlert } from "lucide-react";

export default function HostDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);

  useEffect(() => {
    api.get("/host/dashboard").then(r => setStats(r.data)).catch(() => {});
    api.get("/host/listings").then(r => setListings(r.data)).catch(() => {});
    api.get("/host/stripe/status").then(r => setStripeStatus(r.data)).catch(() => {});
  }, []);

  const statusColor = {
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    pending_review: "bg-amber-50 text-amber-800 border-amber-200",
    changes_requested: "bg-orange-50 text-orange-800 border-orange-200",
    rejected: "bg-red-50 text-red-800 border-red-200",
    suspended: "bg-slate-100 text-slate-700 border-slate-200",
    draft: "bg-slate-50 text-slate-700 border-slate-200",
  };

  const ready = stripeStatus?.charges_enabled && stripeStatus?.payouts_enabled;

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[var(--fpn-navy)]" data-testid="host-dashboard-title">Host dashboard</h1>
          <p className="text-sm text-slate-600 mt-1">Welcome back, {user?.first_name}.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/host/earnings" className="rounded-full px-5 py-2.5 text-sm border border-slate-200 inline-flex items-center gap-2" data-testid="host-earnings-link"><Wallet size={14}/> Earnings</Link>
          <Link to="/host/list-space" className="btn-amber rounded-full px-6 py-3 text-sm" data-testid="host-list-space-cta">+ List a new space</Link>
        </div>
      </div>

      {!ready && (
        <div className="mt-6 border border-amber-200 bg-amber-50 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="host-connect-banner">
          <div className="flex items-center gap-3 text-sm text-amber-900">
            <CircleAlert size={18}/> Connect your bank account with Stripe to receive payouts from bookings.
          </div>
          <Link to="/host/earnings" className="btn-navy rounded-full px-5 py-2 text-sm">Set up payouts</Link>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <Stat label="Total listings" value={stats?.listings ?? 0}/>
        <Stat label="Approved" value={stats?.approved_listings ?? 0}/>
        <Stat label="Bookings" value={stats?.bookings ?? 0}/>
        <Stat label="Earnings" value={`£${(stats?.total_earnings ?? 0).toFixed(2)}`}/>
      </div>

      <h2 className="font-display text-xl font-semibold mt-12 text-[var(--fpn-navy)]">Your listings</h2>
      <div className="mt-4 space-y-3">
        {listings.length === 0 && <div className="text-sm text-slate-500 p-6 border border-dashed border-slate-200 rounded-2xl" data-testid="host-listings-empty">You have no listings yet. Get started by listing your first space.</div>}
        {listings.map(l => (
          <div key={l.id} className="border border-slate-200 rounded-2xl p-5 flex justify-between items-center flex-wrap gap-4 bg-white" data-testid={`host-listing-${l.id}`}>
            <div>
              <div className="font-semibold text-[var(--fpn-navy)]">{l.title}</div>
              <div className="text-sm text-slate-500">{l.airport_code} · £{l.base_daily_price}/day · Capacity {l.capacity}</div>
            </div>
            <span className={`text-[11px] font-bold px-2 py-1 rounded-md tracking-widest border uppercase ${statusColor[l.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{l.status.replace("_", " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="border border-slate-200 rounded-2xl p-5 bg-white">
    <div className="text-xs text-slate-500 uppercase tracking-widest">{label}</div>
    <div className="font-display text-3xl font-bold text-[var(--fpn-navy)] mt-2">{value}</div>
  </div>
);
